import { DeleteFilled } from '@ant-design/icons';
import { Col, Form, FormListFieldData, FormListOperation, Input, Row, Space, Typography } from 'antd';
import classNames from 'classnames';
import React, { memo, useContext } from 'react';

import IconLabel from 'components/atoms/IconLabel';
import DefaultBackendSearchSelect from 'components/composites/DefaultBackendSearchSelect';
import { FormContext } from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import EnterpriseLink from 'components/composites/EnterpriseLink';
import { BackOfficeInvitationFormItems } from 'components/forms/BackOfficeInvitationFormItems';
import ContactFormItems from 'components/forms/ContactFormItems';
import FieldValue from 'components/forms/FieldValue';
import FormCheckbox from 'components/forms/FormCheckbox';
import FormItemCol from 'components/forms/FormItemCol';
import FormItemRole from 'components/forms/FormItemRole';
import { INTERNAL_ADMIN_ROLE_TO_LABEL } from 'constants/internal-admin-roles';
import useTranslation from 'hooks/useTranslation';
import { useMyUser } from 'store/modules/myUser/hooks';
import { SearchTypes } from 'store/modules/search/types';
import Enterprise from 'types/models/enterprise';
import { UserRoleLabels } from 'types/models/membership';
import { userHasTerritoryAccessToEnterprise } from 'utils/AccessControl/user-has-territory-access-to-enterprise';

import { InternalAdminRoleFieldValue } from './components/InternalAdminRoleFieldValue';
import { InternalAdminRoleSelectWithConfirmation } from './components/InternalAdminRoleSelectWithConfirmation';

import './styles.less';

const formCheckboxStyling = { marginTop: '-10px', marginBottom: '-10px' };

interface Props {
  /** Use responsive column width */
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  readOnly: boolean;
}

function BackOfficeUserFormItems({
  isFullSpanOnMobileHalfOnDesktop,
  readOnly
}: Props) {
  const { formInstance } = useContext(FormContext);
  const myUser = useMyUser();
  const { t } = useTranslation();
  return (
    <Row gutter={24}>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <DefaultFormItem
          name="username"
          label="Username"
          readOnly={readOnly}
          rules={[
            {
              required: true,
              message: 'Please enter a username'
            }
          ]}
        >
          <Input />
        </DefaultFormItem>
      </FormItemCol>

      <FieldValue name="enableSmsNotifications">
        {(enableSmsNotifications) => (
          <ContactFormItems
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
            phoneRequired={!!enableSmsNotifications}
            readOnly={readOnly}
          />
        )}
      </FieldValue>
      <Col span={24}>
        <InternalAdminRoleFieldValue>
          {/* Allow to remove or switch among the internal admin roles only if user is already an internal admin */}
          {(userIsInternalAdmin: boolean) => userIsInternalAdmin && (
            <DefaultFormItem
              name="adminRole"
              label="Internal Admin Role"
              readOnly={readOnly}
              formatReadOnlyValue={value => INTERNAL_ADMIN_ROLE_TO_LABEL[value]}
              required
            >
              <InternalAdminRoleSelectWithConfirmation
                // Reset to initial value and clear dirty state
                onDismiss={() => formInstance?.resetFields(['adminRole'])}
              />
            </DefaultFormItem>
          )}
        </InternalAdminRoleFieldValue>
      </Col>
      <Col span={24}>
        <Typography.Text strong>Memberships</Typography.Text>
      </Col>
      <InternalAdminRoleFieldValue>
        {/* Display of 'Memberships' is dependent on 'Admin Role', hence it is wrapped in a FieldValue component. */}
        {(userIsInternalAdmin: boolean) => (
          <Form.List name="memberships">
            {/*
            TODO: Now that we have updated Ant Design, Form.List can take
            'initialValues' & 'rules' as props. These may be needed, or make
            the current logic clearer, when we replace
            DefaultBackendSearchSelect with Select.
            */}
            {(fields: FormListFieldData[], { remove }: FormListOperation) => {
              const membershipsData = formInstance?.getFieldValue('memberships');

              const isMembershipInMyTerritoryAccess = (fieldKey: FormListFieldData['key']): boolean => {
                const membership = membershipsData ? membershipsData[fieldKey] : undefined;
                if (!membership?.enterprise) return false;
                return userHasTerritoryAccessToEnterprise(myUser, membership.enterprise);
              };

              return fields.length ? (
                // Each membership is a row in the form
                fields.map(({ key: index }) => {
                  const inTerritoryAccess = isMembershipInMyTerritoryAccess(index);

                  const subdomainFieldPath = ['memberships', index, 'enterprise', 'subdomain'];
                  const enterpriseNameFieldPath = ['memberships', index, 'enterprise', 'name'];
                  const roleFieldPath = ['memberships', index, 'role'];

                  const subdomainAsLink = (
                    <FieldValue name={subdomainFieldPath}>
                      {(subdomain) => (
                        <EnterpriseLink
                          disabled={!subdomain || !inTerritoryAccess}
                          enterprise={{ subdomain }}
                        />
                      )}
                    </FieldValue>
                  );

                  if (readOnly || !inTerritoryAccess) {
                    const enterpriseNameAsText = (
                      <FieldValue name={enterpriseNameFieldPath} />
                    );
                    const roleAsText = (
                      <FieldValue name={roleFieldPath}>
                        {(role) => UserRoleLabels[role]}
                      </FieldValue>
                    );
                    return (
                      <Col span={24} key={index} className="mt-8 mb-16">
                        <IconLabel
                          gap={12}
                          icon={subdomainAsLink}
                          label={<Typography>{enterpriseNameAsText} · {roleAsText}</Typography>}
                        />
                      </Col>
                    );
                  }

                  const changeMembershipEnterprise = (enterprise: Enterprise) => {
                    // Get membership being edited.
                    const values = formInstance?.getFieldsValue();
                    const { memberships } = values;
                    const membership = memberships[index];
                    // Set new enterprise ID, remove enterprise object from membership.
                    membership.enterpriseId = enterprise.id;
                    membership.enterprise = enterprise;
                    // delete membership ID if present.
                    delete membership.id;

                    // Add edited membership back to form.
                    memberships[index] = membership;
                    formInstance?.setFieldsValue({ memberships });
                  };
                  return (
                    <React.Fragment key={index}>
                      <Col
                        span={2}
                        style={{ marginTop: 13 }}
                      >
                        {subdomainAsLink}
                      </Col>
                      <Col
                        span={11}
                        className="memberships"
                      >
                        <DefaultFormItem>
                          <FormItemCol
                            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
                          >
                            <FieldValue name={enterpriseNameFieldPath}>
                              {(enterpriseName) => (
                                <DefaultBackendSearchSelect
                                  disabled={userIsInternalAdmin}
                                  placeholder="Enterprise"
                                  initialValue={enterpriseName as string}
                                  onItemSelect={changeMembershipEnterprise}
                                  searchType={
                                    SearchTypes.
                                      UserEnterpriseMembershipSearch
                                  }
                                  labelProperty="name"
                                  name={[index, 'enterpriseId']}
                                  rules={[
                                    {
                                      required: !userIsInternalAdmin,
                                      message:
                                        'Please enter an enterprise.'
                                    }
                                  ]}
                                />
                              )}
                            </FieldValue>
                          </FormItemCol>
                        </DefaultFormItem>
                      </Col>

                      <Col
                        span={9}
                        className="memberships"
                      >
                        <FormItemRole
                          label=""
                          name={[index, 'role']}
                          rules={[
                            {
                              required: !userIsInternalAdmin,
                              message: 'Please enter a role.'
                            }
                          ]}
                          disabled={userIsInternalAdmin}
                        />
                      </Col>
                      {/* We allow deletion of all memberships for users */}
                      <Col span={1}>
                        <DeleteFilled
                          onClick={() => remove(index)}
                          className={classNames('membershipBin', {
                            initialBin: index === 0
                          })}
                        />
                      </Col>
                    </React.Fragment>
                  );
                })
              ) : (
                <div
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    margin: '16px'
                  }}
                >
                  No Memberships
                </div>
              );
            }}
          </Form.List>
        )}
      </InternalAdminRoleFieldValue>
      {!readOnly && (<Col span={24}>
        <InternalAdminRoleFieldValue>
          {/* Display of 'Invitations' is dependent on 'Admin Role', hence it is wrapped in a FieldValue component. */}
          {(userIsInternalAdmin: boolean) => (
            <FieldValue name="memberships">
              {(memberships) => (
                <BackOfficeInvitationFormItems
                  disabled={userIsInternalAdmin}
                  showInvitationList
                />
              )}
            </FieldValue>
          )}
        </InternalAdminRoleFieldValue>
      </Col>)}
      <Col span={24}>
        <Space direction="vertical">
          <Typography.Text strong>Notifications Preferences</Typography.Text>
          <Typography.Text>Send alert notifications via</Typography.Text>
          <FormCheckbox
            fieldName="enableEmailNotifications"
            label="Email"
            style={formCheckboxStyling}
            readOnly={readOnly}
          />
          <FormCheckbox
            fieldName="enableSmsNotifications"
            label={t('SMS')}
            style={formCheckboxStyling}
            readOnly={readOnly}
          />
        </Space>
      </Col>
    </Row>
  );
}

export default memo(BackOfficeUserFormItems);
